"use client";

import { type ReactNode, useCallback } from "react";
import { CREATE_DASHBOARD_NAME } from "@/lib/ai/tools/create-dashboard/constants";
import { CREATE_INDEX_WORKER_NAME } from "@/lib/ai/tools/create-index-worker/constants";
import { CREATE_PROJECT_NAME } from "@/lib/ai/tools/create-project/constants";
import { CREATE_SA_BIGQUERY_NAME } from "@/lib/ai/tools/create-search-analytics-bigquery/constants";
import { CREATE_SA_VIEW_NAME } from "@/lib/ai/tools/create-search-analytics-view/constants";
import { CREATE_UA_BIGQUERY_NAME } from "@/lib/ai/tools/create-user-analytics-bigquery/constants";
import { CREATE_UA_VIEW_NAME } from "@/lib/ai/tools/create-user-analytics-view/constants";
import { DELETE_DASHBOARD_NAME } from "@/lib/ai/tools/delete-dashboard/constants";
import { DELETE_INDEX_WORKER_NAME } from "@/lib/ai/tools/delete-index-worker/constants";
import { DELETE_PROJECT_NAME } from "@/lib/ai/tools/delete-project/constants";
import { DELETE_SA_VIEW_NAME } from "@/lib/ai/tools/delete-search-analytics-view/constants";
import { DELETE_UA_VIEW_NAME } from "@/lib/ai/tools/delete-user-analytics-view/constants";
import { DUPLICATE_INDEX_WORKER_NAME } from "@/lib/ai/tools/duplicate-index-worker/constants";
import { DUPLICATE_PROJECT_NAME } from "@/lib/ai/tools/duplicate-project/constants";
import { EDIT_DASHBOARD_NAME } from "@/lib/ai/tools/edit-dashboard/constants";
import { EDIT_INDEX_WORKER_NAME } from "@/lib/ai/tools/edit-index-worker/constants";
import { EDIT_PROJECT_NAME } from "@/lib/ai/tools/edit-project/constants";
import { MOVE_INDEX_WORKER_PROJECT_NAME } from "@/lib/ai/tools/move-index-worker-project/constants";
import {
  type ToolSuccessEffect,
  ToolSuccessEffectsProvider,
} from "@/lib/ai/tools/ui/tool-success-effects-context";
import { UPDATE_SA_VIEW_NAME } from "@/lib/ai/tools/update-search-analytics-view/constants";
import { UPDATE_UA_VIEW_NAME } from "@/lib/ai/tools/update-user-analytics-view/constants";
import { useRefreshWorkspaceSidebar } from "../../../_contexts/workspace-sidebar-data-context";

const SIDEBAR_MUTATION_TOOL_NAMES = new Set<string>([
  CREATE_PROJECT_NAME,
  EDIT_PROJECT_NAME,
  DELETE_PROJECT_NAME,
  DUPLICATE_PROJECT_NAME,
  CREATE_DASHBOARD_NAME,
  EDIT_DASHBOARD_NAME,
  DELETE_DASHBOARD_NAME,
  CREATE_INDEX_WORKER_NAME,
  EDIT_INDEX_WORKER_NAME,
  DELETE_INDEX_WORKER_NAME,
  DUPLICATE_INDEX_WORKER_NAME,
  MOVE_INDEX_WORKER_PROJECT_NAME,
  CREATE_SA_VIEW_NAME,
  UPDATE_SA_VIEW_NAME,
  DELETE_SA_VIEW_NAME,
  CREATE_SA_BIGQUERY_NAME,
  CREATE_UA_VIEW_NAME,
  UPDATE_UA_VIEW_NAME,
  DELETE_UA_VIEW_NAME,
  CREATE_UA_BIGQUERY_NAME,
]);

/** Connects successful sidebar-changing AI tools to the workspace snapshot. */
export function WorkspaceSidebarAiEffects({
  children,
}: {
  children: ReactNode;
}): ReactNode {
  const refreshWorkspaceSidebar = useRefreshWorkspaceSidebar();
  const effect = useCallback<ToolSuccessEffect>(
    ({ toolName }) => {
      if (!SIDEBAR_MUTATION_TOOL_NAMES.has(toolName)) return;
      refreshWorkspaceSidebar("ai-tool-mutation");
    },
    [refreshWorkspaceSidebar],
  );

  return (
    <ToolSuccessEffectsProvider effect={effect}>
      {children}
    </ToolSuccessEffectsProvider>
  );
}
